/**
 * ═══════════════════════════════════════════════════════════════════════════
 * useContactNames.ts - Hook para gestión de nombres de contactos
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @description Hook personalizado para manejar nombres de contactos
 * @version 1.0.0
 * @architecture WASP Frontend
 * 
 * ESTADO ACTUAL:
 * - ✅ Usa localStorage para persistencia local
 * - ✅ Sincronización con estado React
 * - ✅ API lista para integración futura
 * 
 * TODO: Integrar con endpoints cuando estén disponibles:
 * - POST /api/whatsapp/wasp/contact/save       - Guardar nombre
 * - GET  /api/whatsapp/wasp/contact/all        - Listar todos
 * - POST /api/whatsapp/wasp/contact/batch      - Batch de nombres
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { useState, useEffect, useCallback } from 'react';

// Clave para localStorage
const STORAGE_KEY = 'wasp_contact_names';

// Tipos
export interface ContactName {
  whatsappId: string;
  name: string;
  updatedAt: string;
}

export interface ContactNamesMap {
  [whatsappId: string]: string;
}

export interface UseContactNamesReturn {
  contactNames: ContactNamesMap;
  getContactName: (whatsappId: string, fallback?: string) => string;
  setContactName: (whatsappId: string, name: string) => Promise<boolean>;
  loadContactNames: () => void;
  clearContactNames: () => void;
  hasContactName: (whatsappId: string) => boolean;
}

/**
 * Hook personalizado para gestión de nombres de contactos
 * 
 * @example
 * ```tsx
 * const { contactNames, getContactName, setContactName } = useContactNames();
 * 
 * // Obtener nombre
 * const displayName = getContactName('5698123456', 'Usuario');
 * 
 * // Guardar nombre
 * await setContactName('5698123456', 'Jorge');
 * ```
 */
export const useContactNames = (): UseContactNamesReturn => {
  // Estado del mapa de nombres
  const [contactNames, setContactNames] = useState<ContactNamesMap>({});

  /**
   * Cargar nombres desde localStorage al montar el componente
   */
  useEffect(() => {
    loadContactNames();
  }, []);

  /**
   * Cargar nombres desde localStorage
   */
  const loadContactNames = useCallback((): void => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        setContactNames(parsed);
        console.log('✅ Nombres de contactos cargados desde localStorage:', Object.keys(parsed).length);
      }
    } catch (error) {
      console.error('❌ Error cargando nombres de contactos:', error);
    }
  }, []);

  /**
   * Guardar nombres en localStorage
   */
  const saveToStorage = useCallback((names: ContactNamesMap): void => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(names));
      console.log('✅ Nombres guardados en localStorage:', Object.keys(names).length);
    } catch (error) {
      console.error('❌ Error guardando nombres:', error);
    }
  }, []);

  /**
   * Obtener nombre de un contacto
   * 
   * @param whatsappId - ID de WhatsApp del contacto
   * @param fallback - Nombre por defecto si no existe
   * @returns Nombre del contacto o fallback
   */
  const getContactName = useCallback(
    (whatsappId: string, fallback?: string): string => {
      return contactNames[whatsappId] || fallback || whatsappId;
    },
    [contactNames]
  );

  /**
   * Establecer nombre de un contacto
   * 
   * @param whatsappId - ID de WhatsApp del contacto
   * @param name - Nuevo nombre
   * @returns Promise<boolean> - true si se guardó correctamente
   * 
   * TODO: Cuando haya endpoint, hacer POST a /api/whatsapp/wasp/contact/save
   */
  const setContactName = useCallback(
    async (whatsappId: string, name: string): Promise<boolean> => {
      try {
        // Validación básica
        if (!whatsappId || !name.trim()) {
          console.warn('⚠️ WhatsAppId o nombre inválido');
          return false;
        }

        // Actualizar estado local
        const updatedNames = {
          ...contactNames,
          [whatsappId]: name.trim()
        };

        setContactNames(updatedNames);
        saveToStorage(updatedNames);

        console.log(`✅ Nombre actualizado: ${whatsappId} → ${name}`);

        // TODO: Cuando haya endpoint backend, descomentar y usar:
        /*
        const token = localStorage.getItem('authToken'); // O desde contexto
        const response = await axios.post(
          'https://backend.anubisai.net/api/whatsapp/wasp/contact/save',
          { whatsappId, name: name.trim() },
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );

        if (!response.data.success) {
          console.error('❌ Error en servidor al guardar nombre');
          return false;
        }
        */

        return true;
      } catch (error) {
        console.error('❌ Error guardando nombre de contacto:', error);
        return false;
      }
    },
    [contactNames, saveToStorage]
  );

  /**
   * Verificar si existe nombre para un contacto
   * 
   * @param whatsappId - ID de WhatsApp del contacto
   * @returns true si existe nombre guardado
   */
  const hasContactName = useCallback(
    (whatsappId: string): boolean => {
      return !!contactNames[whatsappId];
    },
    [contactNames]
  );

  /**
   * Limpiar todos los nombres (útil para debugging o logout)
   */
  const clearContactNames = useCallback((): void => {
    setContactNames({});
    localStorage.removeItem(STORAGE_KEY);
    console.log('🗑️ Nombres de contactos limpiados');
  }, []);

  /**
   * TODO: Función para cargar nombres desde servidor (batch)
   * Cuando el endpoint esté disponible:
   * 
   * const loadFromServer = async (whatsappIds: string[]) => {
   *   const token = localStorage.getItem('authToken');
   *   const response = await axios.post(
   *     'https://backend.anubisai.net/api/whatsapp/wasp/contact/batch',
   *     { whatsappIds },
   *     { headers: { 'Authorization': `Bearer ${token}` } }
   *   );
   *   
   *   if (response.data.success && response.data.contacts) {
   *     const serverNames: ContactNamesMap = {};
   *     response.data.contacts.forEach((contact: ContactName) => {
   *       serverNames[contact.whatsappId] = contact.name;
   *     });
   *     
   *     setContactNames(prev => ({ ...prev, ...serverNames }));
   *     saveToStorage({ ...contactNames, ...serverNames });
   *   }
   * };
   */

  return {
    contactNames,
    getContactName,
    setContactName,
    loadContactNames,
    clearContactNames,
    hasContactName
  };
};

export default useContactNames;
